import { mapLearningPackage } from '../../Learning/helpers/mapLearningPackage';
import { LEARNING_PHASES } from '../../Learning/constants/learningPhases';
import { learningDefaults } from '../../Learning/constants/learningDefaults';

export const selectLearningState = (state) => state.learning;

export const selectLearningPackageId = (state) => state.learning.packageId;
export const selectLearningUserId = (state) => state.learning.userId;
export const selectLearningSurveyId = (state) => state.learning.surveyId;

export const selectLearningPackageData = (state) => state.learning.packageData;
export const selectLearningPackageLoading = (state) => state.learning.packageLoading;
export const selectLearningPackageError = (state) => state.learning.packageError;

export const selectLearningPhase = (state) => state.learning.phase;
/** Completed quiz submissions for this invite (0 until first submit). */
export const selectLearningAttemptsUsed = (state) => state.learning.attemptsUsed ?? 0;
export const selectLearningInviteLocked = (state) => state.learning.isInviteLocked ?? false;
export const selectLearningRecipientEmail = (state) => state.learning.recipientEmail ?? '';
export const selectLearningQuizResult = (state) => state.learning.quizResult;

/** 1-based attempt number for the next quiz submission. */
export function selectLearningNextSubmitAttempt(state) {
  return selectLearningAttemptsUsed(state) + 1;
}

/** Last completed attempt shown on outcome screens (same as attemptsUsed when > 0). */
export function selectLearningAttemptNumber(state) {
  return selectLearningAttemptsUsed(state);
}

export const selectLearningGuidance = (state) => state.learning.guidance;
export const selectLearningQuestionnaire = (state) => state.learning.questionnaire;
export const selectSurveyAnswers = (state) => state.survey?.answeres || {};

let cachedPackageData = null;
let cachedCourse = null;

export function selectLearningCourse(state) {
  const packageData = selectLearningPackageData(state);

  if (!packageData) {
    cachedPackageData = null;
    cachedCourse = null;
    return null;
  }

  if (packageData === cachedPackageData) {
    return cachedCourse;
  }

  cachedPackageData = packageData;
  cachedCourse = mapLearningPackage(packageData);
  return cachedCourse;
}

export function selectEffectiveLearningSurveyId(state) {
  const surveyId = selectLearningSurveyId(state);
  const course = selectLearningCourse(state);
  return surveyId || course?.surveyId || null;
}

export function selectLearningPassMarkPercent(state) {
  const course = selectLearningCourse(state);
  return course?.passMarkPercent ?? learningDefaults.passMarkPercent;
}

export function selectLearningMaxAttempts(state) {
  const course = selectLearningCourse(state);
  return course?.maxAttempts ?? learningDefaults.maxAttempts;
}

export function selectLearningAttemptsRemaining(state) {
  const attemptsUsed = selectLearningAttemptsUsed(state);
  const maxAttempts = selectLearningMaxAttempts(state);
  return Math.max(0, maxAttempts - attemptsUsed);
}

export function selectLearningHasPassed(state) {
  const phase = selectLearningPhase(state);
  const quizResult = selectLearningQuizResult(state);
  return phase === LEARNING_PHASES.SUCCESS || quizResult?.passed === true;
}

export function selectLearningCanRetry(state) {
  const phase = selectLearningPhase(state);
  const attemptsUsed = selectLearningAttemptsUsed(state);
  const maxAttempts = selectLearningMaxAttempts(state);

  if (selectLearningInviteLocked(state) || phase === LEARNING_PHASES.LOCKED) {
    return false;
  }

  if (selectLearningHasPassed(state)) {
    return false;
  }

  if (attemptsUsed >= maxAttempts) {
    return false;
  }

  return phase === LEARNING_PHASES.FAIL;
}

export function selectLearningCanStartFlow(state) {
  if (selectLearningInviteLocked(state)) {
    return false;
  }

  if (selectLearningHasPassed(state)) {
    return false;
  }

  const attemptsUsed = selectLearningAttemptsUsed(state);
  const maxAttempts = selectLearningMaxAttempts(state);

  if (attemptsUsed >= maxAttempts) {
    return false;
  }

  const phase = selectLearningPhase(state);
  return phase !== LEARNING_PHASES.LOCKED && phase !== LEARNING_PHASES.SUCCESS;
}

export const selectLearningQuestionnaireSurvey = (state) => state.learning.questionnaire.survey;
export const selectLearningQuestionnaireLoading = (state) => state.learning.questionnaire.loading;
export const selectLearningQuestionnaireError = (state) => state.learning.questionnaire.error;
export const selectLearningQuestionIndex = (state) => state.learning.questionnaire.questionIndex;
export const selectLearningQuestionnaireSubmitted = (state) => state.learning.questionnaire.submitted;

export const selectLearningBrandLogo = (state) => (
  state.learning.brand?.logoSrc || learningDefaults.logoSrc
);

export const selectLearningBrandPackageId = (state) => state.learning.brand?.packageId;
